import { useEffect, useState } from 'react'
import { Button, Checkbox, TextInput } from '../../Common'
import { Select } from '../../Common/Select'
import { PAGE_LABEL_KEYS } from '../../../utils/memberControlsPageLabels'
import {
  applyControlProfile,
  setMemberControlsBulk,
  subscribeToControlProfiles,
} from '../../../services/memberControlsService'
import {
  HIDEABLE_TAB_KEYS,
  type AiControlMode,
  type ChatControl,
  type ControlProfile,
  type FunPackControl,
  type MemberControls,
} from '../../../types/memberControls'
import type { WorkspaceRole } from '../../../types/workspace'
import { useNls } from '../../../nls/nlsProvider'
import './MemberBulkControlsBar.css'

export interface MemberBulkControlsBarProps {
  workspaceId: string
  selectedMembers: Array<{
    id: string
    uid: string | null
    role: WorkspaceRole
    displayName: string
  }>
  isOwnerViewer: boolean
  onClearSelection: () => void
  onSuccess?: () => void
}

export const MemberBulkControlsBar = ({
  workspaceId,
  selectedMembers,
  isOwnerViewer,
  onClearSelection,
  onSuccess,
}: MemberBulkControlsBarProps) => {
  const { t } = useNls()
  const [profiles, setProfiles] = useState<ControlProfile[]>([])
  const [isApplyProfileOpen, setIsApplyProfileOpen] = useState(false)
  const [isSetControlOpen, setIsSetControlOpen] = useState(false)
  const [selectedProfileId, setSelectedProfileId] = useState<string>('')

  const [controlType, setControlType] = useState<'funPack' | 'chat' | 'ai' | 'tiles'>('chat')
  const [funPackVal, setFunPackVal] = useState<FunPackControl>('INHERIT')
  const [chatVal, setChatVal] = useState<ChatControl>('FULL')
  const [aiModeVal, setAiModeVal] = useState<AiControlMode>('INHERIT')
  const [aiDailyCredits, setAiDailyCredits] = useState<string>('50')
  const [tilesVal, setTilesVal] = useState<string[]>([])

  const [resultMessage, setResultMessage] = useState<string | null>(null)
  const [isBusy, setIsBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!workspaceId) return undefined
    return subscribeToControlProfiles(workspaceId, (fetched) => {
      setProfiles(fetched)
    })
  }, [workspaceId])

  const partitionTargets = () => {
    const skippedTargets: typeof selectedMembers = []
    const eligibleTargets: string[] = []

    for (const m of selectedMembers) {
      if (m.role === 'OWNER') {
        skippedTargets.push(m)
        continue
      }
      if (m.role === 'ADMIN' && !isOwnerViewer) {
        skippedTargets.push(m)
        continue
      }
      const targetId = m.uid || m.id
      if (targetId) {
        eligibleTargets.push(targetId)
      }
    }

    return {
      preSkippedCount: skippedTargets.length,
      targetUserIds: eligibleTargets,
    }
  }

  const handleApplyProfile = async () => {
    setIsBusy(true)
    setResultMessage(null)
    setError(null)
    try {
      const { preSkippedCount, targetUserIds } = partitionTargets()
      let serverUpdated = 0
      let serverSkipped = 0
      let serverFailed = 0

      if (targetUserIds.length > 0) {
        const res = await applyControlProfile({
          workspaceId,
          profileId: selectedProfileId || null,
          targetUserIds,
        })
        serverUpdated = res.updated
        serverSkipped = res.skipped
        serverFailed = res.failed
      }

      const totalSkipped = preSkippedCount + serverSkipped
      setResultMessage(
        t('NLSControlsBulkResult')
          .replace('{updated}', String(serverUpdated))
          .replace('{skipped}', String(totalSkipped))
          .replace('{failed}', String(serverFailed)),
      )
      setIsApplyProfileOpen(false)
      onSuccess?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not apply profile')
    } finally {
      setIsBusy(false)
    }
  }

  const handleSetControl = async () => {
    setIsBusy(true)
    setResultMessage(null)
    setError(null)
    try {
      const { preSkippedCount, targetUserIds } = partitionTargets()
      let patch: Partial<MemberControls> = {}

      if (controlType === 'funPack') {
        patch = { funPack: funPackVal }
      } else if (controlType === 'chat') {
        patch = { chat: chatVal }
      } else if (controlType === 'ai') {
        if (aiModeVal === 'BUDGET') {
          patch = { ai: { mode: 'BUDGET', dailyCredits: Math.max(0, Number(aiDailyCredits) || 0) } }
        } else {
          patch = { ai: { mode: aiModeVal } }
        }
      } else if (controlType === 'tiles') {
        patch = { tiles: tilesVal }
      }

      let serverUpdated = 0
      let serverSkipped = 0
      let serverFailed = 0

      if (targetUserIds.length > 0) {
        const res = await setMemberControlsBulk({
          workspaceId,
          targetUserIds,
          patch,
        })
        serverUpdated = res.updated
        serverSkipped = res.skipped
        serverFailed = res.failed
      }

      const totalSkipped = preSkippedCount + serverSkipped
      setResultMessage(
        t('NLSControlsBulkResult')
          .replace('{updated}', String(serverUpdated))
          .replace('{skipped}', String(totalSkipped))
          .replace('{failed}', String(serverFailed)),
      )
      setIsSetControlOpen(false)
      onSuccess?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not set controls')
    } finally {
      setIsBusy(false)
    }
  }

  return (
    <div className="memberBulkControlsBar" data-testid="member-bulk-controls-bar">
      <div className="memberBulkControlsBar__summary">
        <span className="memberBulkControlsBar__count">
          {t('NLSControlsSelectedCount').replace('{count}', String(selectedMembers.length))}
        </span>
        {resultMessage ? (
          <span className="memberBulkControlsBar__result" data-testid="bulk-controls-result">
            {resultMessage}
          </span>
        ) : null}
        {error ? (
          <span className="memberBulkControlsBar__error" role="alert">
            {error}
          </span>
        ) : null}
      </div>

      <div className="memberBulkControlsBar__actions">
        <Button
          variant="secondary"
          size="sm"
          disabled={selectedMembers.length === 0 || isBusy}
          onClick={() => setIsApplyProfileOpen(true)}
        >
          {t('NLSControlsApplyProfile')}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={selectedMembers.length === 0 || isBusy}
          onClick={() => setIsSetControlOpen(true)}
        >
          {t('NLSControlsSetControl')}
        </Button>
        <Button variant="tertiary" size="sm" disabled={isBusy} onClick={onClearSelection}>
          Cancel
        </Button>
      </div>

      {isApplyProfileOpen ? (
        <div className="bulkDialogOverlay" data-testid="bulk-apply-profile-dialog">
          <div className="bulkDialogContent" aria-modal="true" aria-label={t('NLSControlsApplyProfile')}>
            <h3>{t('NLSControlsApplyProfile')}</h3>
            <label className="bulkDialogField">
              <span>{t('NLSControlsSelectProfile')}</span>
              <Select<string>
                value={selectedProfileId}
                onChange={(val) => setSelectedProfileId(val)}
                ariaLabel="Select profile"
                options={[
                  { value: '', label: t('NLSControlsProfileNone') },
                  ...profiles.map((p) => ({ value: p.id || '', label: p.name })),
                ]}
              />
            </label>

            <div className="bulkDialogActions">
              <Button
                variant="primary"
                size="sm"
                disabled={isBusy}
                onClick={handleApplyProfile}
              >
                {isBusy ? 'Applying...' : 'Apply to selected'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={isBusy}
                onClick={() => setIsApplyProfileOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {isSetControlOpen ? (
        <div className="bulkDialogOverlay" data-testid="bulk-set-control-dialog">
          <div className="bulkDialogContent" aria-modal="true" aria-label={t('NLSControlsSetControl')}>
            <h3>{t('NLSControlsSetControl')}</h3>

            <label className="bulkDialogField">
              <span>{t('NLSControlsControlToSet')}</span>
              <Select<'funPack' | 'chat' | 'ai' | 'tiles'>
                value={controlType}
                onChange={(val) => setControlType(val)}
                ariaLabel="Control to set"
                options={[
                  { value: 'chat', label: t('NLSControlsChat') },
                  { value: 'funPack', label: t('NLSControlsFunPack') },
                  { value: 'ai', label: t('NLSControlsAi') },
                  { value: 'tiles', label: t('NLSControlsPagesBulkOption') },
                ]}
              />
            </label>

            {controlType === 'chat' ? (
              <label className="bulkDialogField">
                <span>{t('NLSControlsChatAccess')}</span>
                <Select<ChatControl>
                  value={chatVal}
                  onChange={(val) => setChatVal(val)}
                  ariaLabel="Chat access"
                  options={[
                    { value: 'FULL', label: t('NLSControlsChatFull') },
                    { value: 'ANNOUNCEMENTS_ONLY', label: t('NLSControlsChatAnnouncementsOnly') },
                    { value: 'NO_DM', label: t('NLSControlsChatNoDm') },
                    { value: 'OFF', label: t('NLSControlsChatOff') },
                  ]}
                />
              </label>
            ) : null}

            {controlType === 'funPack' ? (
              <label className="bulkDialogField">
                <span>{t('NLSControlsFunPack')}</span>
                <Select<FunPackControl>
                  value={funPackVal}
                  onChange={(val) => setFunPackVal(val)}
                  ariaLabel="Fun pack"
                  options={[
                    { value: 'INHERIT', label: t('NLSControlsFunPackInherit') },
                    { value: 'OFF', label: t('NLSControlsFunPackOff') },
                  ]}
                />
              </label>
            ) : null}

            {controlType === 'ai' ? (
              <>
                <label className="bulkDialogField">
                  <span>{t('NLSControlsAi')}</span>
                  <Select<AiControlMode>
                    value={aiModeVal}
                    onChange={(val) => setAiModeVal(val)}
                    ariaLabel="AI mode"
                    options={[
                      { value: 'INHERIT', label: t('NLSControlsAiInherit') },
                      { value: 'OFF', label: t('NLSControlsAiOff') },
                      { value: 'BUDGET', label: t('NLSControlsAiBudget') },
                    ]}
                  />
                </label>
                {aiModeVal === 'BUDGET' ? (
                  <label className="bulkDialogField">
                    <span>{t('NLSControlsAiDailyCredits')}</span>
                    <TextInput
                      type="number"
                      value={aiDailyCredits}
                      onChange={(val) => setAiDailyCredits(val)}
                      aria-label={t('NLSControlsAiDailyCredits')}
                    />
                  </label>
                ) : null}
              </>
            ) : null}

            {controlType === 'tiles' ? (
              <div className="bulkDialogField">
                <span>{t('NLSControlsVisiblePages')}</span>
                <span className="bulkDialogHelp">{t('NLSControlsVisiblePagesDesc')}</span>
                <div className="bulkDialogPagesList">
                  {HIDEABLE_TAB_KEYS.map((key) => {
                    const isVisible = !tilesVal.includes(key)
                    const labelKey = PAGE_LABEL_KEYS[key]
                    const labelText = labelKey ? t(labelKey) : key
                    return (
                      <Checkbox
                        key={key}
                        checked={isVisible}
                        aria-label={labelText}
                        label={labelText}
                        onChange={(nextChecked) => {
                          if (nextChecked) {
                            setTilesVal((prev) => prev.filter((k) => k !== key))
                          } else {
                            setTilesVal((prev) => (prev.includes(key) ? prev : [...prev, key]))
                          }
                        }}
                      />
                    )
                  })}
                </div>
              </div>
            ) : null}

            <div className="bulkDialogActions">
              <Button
                variant="primary"
                size="sm"
                disabled={isBusy}
                onClick={handleSetControl}
              >
                {isBusy ? 'Applying...' : 'Apply to selected'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={isBusy}
                onClick={() => setIsSetControlOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
